import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import {
  ChevronDown, Phone, ArrowRight, Menu, X,
  Wifi, Shield, Database, Cloud, Headphones, Activity,
  Network, Radio, Camera, Zap, Cable, Settings, BarChart3, RefreshCw, FileCheck
} from 'lucide-react'
import { gsap } from 'gsap'
import styles from './Header.module.css'
import nexcomLogo from '../assets/Logo/sa-technologies.webp'

const services = [
  {
    category: 'Connectivity',
    items: [
      { label: 'High Speed Fiber', href: '/services#connectivity', icon: Wifi, desc: 'Ultra-fast fiber internet for your business.' },
      { label: 'LAN/WAN Solutions', href: '/services#lan', icon: Network, desc: 'Secure local and wide area networks.' },
      { label: 'Voice Solutions', href: '/services#voice-solution', icon: Radio, desc: 'Clear and reliable communication.' },
    ]
  },
  {
    category: 'Infrastructure',
    items: [
      { label: 'Data Center', href: '/services#data-center', icon: Database, desc: 'Secure hosting for critical IT assets.' },
      { label: 'Structured Cabling', href: '/services#structured-cabling', icon: Cable, desc: 'Future-proof network backbone.' },
      { label: 'Power Backup UPS', href: '/services#power-backup-ups', icon: Zap, desc: 'Continuous uptime for your operations.' },
    ]
  },
  {
    category: 'Security & Monitoring',
    items: [
      { label: 'Cyber Security', href: '/services#cyber-security', icon: Shield, desc: 'Advanced protection from cyber threats.' },
      { label: 'Video Surveillance', href: '/services#video-surveillance', icon: Camera, desc: 'Smart monitoring for peace of mind.' },
      { label: 'Network Monitoring', href: '/services#network-monitoring', icon: Activity, desc: 'Proactive detection and resolution.' },
    ]
  }
]

export default function Header() {
  const [scrolled, setScrolled] = useState(false)
  const [visible, setVisible] = useState(true)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [activeMenu, setActiveMenu] = useState(null)
  const [mobileDrop, setMobileDrop] = useState(null)
  const headerRef = useRef(null)
  const megaMenuRef = useRef(null)
  const lastScrollY = useRef(0)
  const location = useLocation()
  const isHome = location.pathname === '/'

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      setScrolled(currentScrollY > 50)

      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setVisible(false)
      } else {
        setVisible(true)
      }
      lastScrollY.current = currentScrollY
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setActiveMenu(null)
    setMobileOpen(false)
    setMobileDrop(null)
  }, [location])

  useEffect(() => {
    if (activeMenu && megaMenuRef.current) {
      gsap.fromTo(megaMenuRef.current, 
        { opacity: 0, y: -10 },
        { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' }
      )
    }
  }, [activeMenu])

  const renderMegaMenu = (data) => (
    <div 
      className={styles.megaMenu} 
      ref={megaMenuRef}
      onMouseEnter={() => setActiveMenu(activeMenu)}
      onMouseLeave={() => setActiveMenu(null)}
    >
      <div className={styles.megaInner}>
        <div className={styles.megaGrid}>
          {data.map((cat, idx) => (
            <div key={idx} className={styles.megaCol}>
              <h4 className={styles.megaCategory}>{cat.category}</h4>
              <ul className={styles.megaList}>
                {cat.items.map((item, i) => (
                  <li key={i}>
                    <Link to={item.href} className={styles.megaLink}>
                      <div className={styles.megaIcon}><item.icon size={18} /></div>
                      <div className={styles.megaItemContent}>
                        <span className={styles.megaLabel}>{item.label}</span>
                        <p className={styles.megaDesc}>{item.desc}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {activeMenu === 'services' && (
            <div className={styles.megaPromo}>
              <div className={styles.megaPromoImage}>
                <img src="/src/assets/Service_Header_Image.webp" alt="Services" />
              </div>
              <div className={styles.megaPromoContent}>
                <h3>Next-Gen Network Solutions</h3>
                <p>Experience the future of connectivity with SA Technologies.</p>
                <Link to="/products" className={styles.megaPromoBtn}>View All Products</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <header 
      ref={headerRef}
      className={`${styles.header} ${isHome ? styles.homeHeader : styles.otherHeader} ${scrolled ? styles.scrolled : ''} ${visible ? styles.visible : styles.hidden} ${mobileOpen ? styles.mobileOpenHeader : ''}`}
    >
      <div className={styles.inner}>
        <Link to="/" className={styles.logo}>
          <img
            src={nexcomLogo}
            alt="SA Technologies"
            className={styles.logoImg}
          />
        </Link>

        <div className={styles.rightSide}>
          <nav className={styles.nav}>
            {/* 
            <div 
              className={styles.navItem}
              onMouseEnter={() => setActiveMenu('services')}
              onMouseLeave={() => setActiveMenu(null)}
            >
              <NavLink to="/services" className={({ isActive }) => `${styles.dropTrigger} ${isActive ? styles.active : ''}`}>
                Services <ChevronDown size={14} className={activeMenu === 'services' ? styles.rotated : ''} />
              </NavLink>
              {activeMenu === 'services' && renderMegaMenu(services)}
            </div>
            */}

            <NavLink to="/products" className={({ isActive }) => isActive ? styles.active : ''}>Products</NavLink>

            <NavLink to="/partners" className={({ isActive }) => isActive ? styles.active : ''}>Partners</NavLink>
          </nav>

          <div className={styles.actions}>
            <a href="tel:+923392450024" className={styles.phone}>
              <Phone size={15} /> <span>+92 339 2450024</span>
            </a>
            <Link to="/contact" className={`btn ${styles.getQuoteBtn} ${styles.desktopOnly}`}>Get Quote</Link>
            
            <button className={styles.burger} onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? (
                <X size={24} />
              ) : (
                <img 
                  src={isHome && !scrolled 
                    ? "/src/assets/Icons/For_Home_Initial_State.svg" 
                    : "/src/assets/Icons/For_Rest_Other_routes_and_Fixed_Header_State.svg"} 
                  alt="Menu"
                  className={styles.burgerIcon}
                />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`${styles.mobileOverlay} ${mobileOpen ? styles.mobileOverlayVisible : ''}`} onClick={() => setMobileOpen(false)} />
      <div className={`${styles.mobileMenu} ${mobileOpen ? styles.mobileOpen : ''}`}>
        <div className={styles.mobileNav}>
          {/*
          <div className={styles.mobileItem}>
            <button className={styles.mobileDropBtn} onClick={() => setMobileDrop(mobileDrop === 'services' ? null : 'services')}>
              Services <ChevronDown size={18} className={mobileDrop === 'services' ? styles.rotated : ''} />
            </button>
            {mobileDrop === 'services' && (
              <div className={styles.mobileDropList}>
                {services.map(cat => (
                  <div key={cat.category} className={styles.mobileSubCat}>
                    <span>{cat.category}</span>
                    {cat.items.map(item => (
                      <Link key={item.label} to={item.href} className={styles.mobileSubLink}>{item.label}</Link>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
          */}

          <NavLink to="/products" className={styles.mobileLink} onClick={() => setMobileOpen(false)}>Products</NavLink>
          <NavLink to="/partners" className={styles.mobileLink} onClick={() => setMobileOpen(false)}>Partners</NavLink>
          <NavLink to="/contact" className={styles.mobileLink} onClick={() => setMobileOpen(false)}>Contact Us</NavLink>
        </div>
        
        <div className={styles.mobileActions}>
          <a href="tel:+923211199937" className={styles.mobilePhone}>
            <Phone size={18} /> +92 321 1199937
          </a>
          <Link to="/contact" className={`btn ${styles.getQuoteBtn}`} style={{ width: '100%', justifyContent: 'center' }}>Get Quote</Link>
        </div>
      </div>
    </header>
  )
}
